
import React, { useEffect } from 'react';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useData } from './context/DataContext';
import Maintenance from './pages/Maintenance';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import QuoteModal from './components/QuoteModal';
import WhatsAppWidget from './components/WhatsAppWidget';
import Home from './pages/Home';
import Inventory from './pages/Inventory';
import Financing from './pages/Financing';
import ROI from './pages/ROI';
import Services from './pages/Services';
import Contact from './pages/Contact';
import About from './pages/About';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import VehicleDetails from './pages/VehicleDetails';

// Admin Imports
import AdminLayout from './pages/admin/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import InventoryManager from './pages/admin/InventoryManager';
import SettingsManager from './pages/admin/SettingsManager';
import BlogManager from './pages/admin/BlogManager';
import EmailConfiguration from './pages/admin/EmailConfiguration';
import EmailMarketing from './pages/admin/EmailMarketing';
import Inquiries from './pages/admin/Inquiries';
import EmailLogs from './pages/admin/EmailLogs';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Small timeout to ensure content is rendered
      setTimeout(() => {
        const element = document.getElementById(hash.replace('#', ''));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
};

// Layout wrapper for public pages to include Nav/Footer
const PublicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { settings } = useData();

  if (settings.maintenanceMode) {
    return <Maintenance />;
  }

  return (
    <div className="flex flex-col min-h-screen font-sans">
      <Navbar />
      <main className="flex-grow">
        {children}
      </main>
      <Footer />
      <WhatsAppWidget />
      <QuoteModal />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
        <Route path="/inventory" element={<PublicLayout><Inventory /></PublicLayout>} />
        <Route path="/inventory/:id" element={<PublicLayout><VehicleDetails /></PublicLayout>} />
        <Route path="/financing" element={<PublicLayout><Financing /></PublicLayout>} />
        <Route path="/roi" element={<PublicLayout><ROI /></PublicLayout>} />
        <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
        <Route path="/blog/:id" element={<PublicLayout><BlogPost /></PublicLayout>} />
        <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
        <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
        <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
        <Route path="/privacy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
        <Route path="/terms" element={<PublicLayout><Terms /></PublicLayout>} />

        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="inventory" element={<InventoryManager />} />
          <Route path="inquiries" element={<Inquiries />} />
          <Route path="blog" element={<BlogManager />} />
          <Route path="email-logs" element={<EmailLogs />} />
          <Route path="email-marketing" element={<EmailMarketing />} />
          <Route path="email-config" element={<EmailConfiguration />} />
          <Route path="settings" element={<SettingsManager />} />
        </Route>
      </Routes>
    </Router>
  );
};

export default App;
